import { BookTitleMatch } from './book_title_match.js';

import { date_range_extractor } from './date_range_extractor.js';

// Converts (renders) standard Minutes text for a single event from
// the Minutes Markup format into an HTML fragment suitable to become the
// innerHTML of some div.

export function render_minutes_markup(
  text,
  is_denson = true,
  denson_year = '1991'
) {
  // Start with basic whitespace cleanup.

  text = text.replace(/\x0b/g, '\n'); // 0x0b (vertical tab) is the encoding for \n in csv "merge" sub-format
  text = text.replace(/(?:(?:\x0D\x0A)|[\x0D\x0A])/g, '<newline>'); // DOS, Mac, or Unix -> \n (along with line 2 below)
  text = text.replace(/[\x00-\x1F\s]+/g, ' '); // Any runs of other control characters, even tabs, should be treated as spaces.
  text = text.replace(/<newline>/g, '\n'); // Finish the newline translation.

  // Remove all runs of spaces at beginnings and ends of all lines.

  text = text.replace(/^ /gm, '');
  text = text.replace(/ $/gm, '');

  // Remove leading and trailing whitespace, newlines, and/or lines of dashes from entire buffer.

  text = text.replace(/^[-\s]+/, '');
  text = text.replace(/[-\s]+$/, '');

  // Add back a single blank line at very end.

  text = text + '\n';

  // Maximum of two consecutive newlines in remaining text.

  text = text.replace(/\n\n+/g, '\n\n');

  // Do some of the steps that would be done during the "import" step...
  text = educate_quotes(text);

  // Split into the 3 header lines, plus the minutes.  Tolerate extra newlines between header rows if present.

  let parts = (text.match(/^([^\n]+)\n+([^\n]+)\n+([^\n]+)\n+([^]+?)[-\s]*$/) ||
    []
  ).slice(1, 5); // slice: drop first item in match results array

  let name = parts[0] || '';
  let location = parts[1] || '';
  let date = parts[2] || '';
  let minutes = parts[3] || '';

  // Convert implied paragraphs to <p>; Surround whole thing by a <div>.
  minutes =
    '<div class="minutes">' +
    '\n' + // eslint-disable-line no-useless-concat
    minutes
      .split(/\n+/)
      .map(function(par) {
        return (
          '<p>' +
          (is_denson
            ? hyperlink_denson_song_number_markups(h(par), denson_year)
            : h(par)) +
          '</p>'
        );
      })
      .join('\n') +
    '\n' +
    '</div>'; // eslint-disable-line no-useless-concat

  //// Handle "markdown"-like text-based markup features ////

  // Em-dashes (Unicode code point 2014)
  // We add a zero-width space (Unicode code point 200B) after long-dashes to allow word breaks there.

  minutes = minutes.replace(/ *-- */g, '\u2014\u200B'); // Double-dash to em-dash.
  minutes = minutes.replace(/ +- +/g, '\u2014\u200B;'); // Single-dash surrounded by whitespace to em-dash.

  // Center lines such as "Saturday [Night], November 18[th]" by making them h5 style
  // The space above the first of these will be slightly more.
  // We also remove any span class (typically span.maybesongnum) that might have been placed on the numeric part, and any ordinal suffixes.
  minutes = minutes.replace(
    /<p>([A-Z][a-z]+(?: +[A-Za-z]+)?, +[A-Z][a-z]+ +)(?:<[^<>]+>)?([0-9]+)(?:<[^<>]+>)?(st)*(nd)*(rd)*(th)*<\/p>/g,
    '<h5>$1 $2</h5>'
  );

  // Handle wiki-like sub-head markups (paragraphs surrounded by == get treated like headings)
  // W3C proposed a "run-in" style to do this properly, but it went out of vogue and was dropped.
  // We could do a bad thing and put a head element inside of a p, which violates nesting rules.  Instead,
  // we will format the he head as a b span inside the p.  There is no good solution.

  minutes = minutes.replace(
    /^<p>== *(.+?)(\.* *==)?<\/p>([^]*?<p>)/gm,
    '<p><b class="h6">$1</b> '
  );

  // format lines starting with (1), (2), or 1), 2), etc. as ol.li:

  minutes = minutes.replace(
    /<p>\(?\d+\) *(.*?)<\/p>/g,
    '<ol>' + '\n' + '<li>$1</li>' + '\n' + '</ol>' // eslint-disable-line no-useless-concat
  );
  minutes = minutes.replace(/\s*<\/ol>\s*<ol>/g, ''); // collapse sequential items into one ol.

  // format lines starting with *, or -, as ul.li:

  minutes = minutes.replace(
    /<p>[*-] *(.*?)<\/p>/g,
    '<ul>' + '\n' + '<li>$1</li>' + '\n' + '</ul>' // eslint-disable-line no-useless-concat
  );
  minutes = minutes.replace(/\s*<\/ul>\s*<ul>/g, ''); // collapse sequential items into one ul.

  // Italic/bold formatting markup: (i blah) or (b blah)

  minutes = minutes.replace(/\(([ib]) +(.*?)\)/gi, '<$1>$2</$1>');

  // Italicize Book Titles (case-sensitive)

  minutes = minutes.replace(
    new RegExp('(' + BookTitleMatch + '[,.;:]*)', 'g'),
    '<i>$1</i>'
  );

  let rendered_html =
    '<div class="minutes_event">' +
    '<h2>' +
    newline_escape_to_br(h(name)) +
    '</h2>' +
    '<h3>' +
    newline_escape_to_br(h(location)) +
    '</h3>' +
    '<h4>' +
    newline_escape_to_br(h(date)) +
    '</h4>' +
    minutes +
    '</div><!-- class="event" -->';

  let dp = date_range_extractor(date); // date properties or null
  let name_line_clean = newline_escape_remove(name);
  let location_line_clean = newline_escape_remove(location);
  let render_results = {
    // main result is the rendered html
    rendered_html: rendered_html,

    // and a useful side effect is parsed metadata
    start_date: dp && dp.start_date,
    end_date: dp && dp.end_date,
    day_count: (dp && dp.day_count) || 0,
    name_line_clean: name_line_clean,
    location_line_clean: location_line_clean
  };

  return render_results;
}

function educate_quotes(text) {
  text = text.replace(/\b'/g, '\u2019'); // Right single aka apostrophe
  text = text.replace(/'\b/g, '\u2018'); // Left single
  text = text.replace(/'/g, '\u2019'); // Right single aka apostrophe
  text = text.replace(/\b"/g, '\u201D'); // Right double
  text = text.replace(/"\b/g, '\u201C'); // Left double
  text = text.replace(/ "/g, ' \u201C'); // space + Left double
  text = text.replace(/" /g, '\u201D '); // Right double + space
  text = text.replace(/"/g, '\u201D'); // Right double
  return text;
}

function h(text) {
  // html-encode: convert plain text to legal html-insertable text.  Assumes text does not contain any HTML markup.
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function newline_escape_to_br(val) {
  val = val.replace(/[,:;\s]*\\n\s*/g, '<br />');
  return val;
}

export function newline_escape_remove(val) {
  val = val.replace(/\s*\\n\s*/g, ' ');
  return val;
}

// hyperlink_denson_song_number_markups

const SHLyricsURL = 'https://fasola.org/indexes/INSERT_YEAR_HERE/?p=';
const bold_match = '(?:</?b.*?>)';
const song_match = '(?:\\d{2,3}[tb]?)';
const goodRE = new RegExp(
  `\\[(${bold_match})?(${song_match})(${bold_match})?\\]`,
  'g'
); // [40]
const goodRE_replacement = `$1<a href="${SHLyricsURL}$2">$2</a>$3`;
const ambiguousRE = new RegExp(
  `\\{(${bold_match})?(${song_match})(${bold_match})?\\}`,
  'g'
); // {569}
const ambiguousRE_replacement = `$1<span class="ambiguous">$2</span> (<a class="ambiguous_link" href="${SHLyricsURL}$2t">t?</a> <a class="ambiguous_link" href="${SHLyricsURL}$2b">b?</a>$3)`;
const correctedRE = new RegExp(
  `\\[(${bold_match})?(${song_match})(${bold_match})?/(${bold_match})?(${song_match})(${bold_match})?\\]`,
  'g'
); // [40t/40]
const correctedRE_replacement = (all, r1, r2, r3, r4, r5, r6) =>
  `${r1 ||
    r4 ||
    ''}<a href="${SHLyricsURL}${r5}" class="corrected">${r2}</a>${r3 ||
    r6 ||
    ''}`;
const badRE = new RegExp(
  `&lt;(${bold_match})?(${song_match})(${bold_match})?&gt;`,
  'g'
); // <255>
const badRE_replacement = '$1<span class="unrecognized">$2 (?)</span></a>$3';

// This style can be used to spot possibly missed numbers... see below.
const any_number_tokenizer = new RegExp(
  '(?:<[^<>]+>)|(?:\\=\\d+)|(?:\\d+:\\d+)|(?:\\$\\d\\S*\\d)|(?:\\b\\d{2,3}[tbTB]?\\b \\(\\w+(?: \\d+)?\\))|(?:\\b\\d{2,3}[tbTB]?\\b)|(?:\\d+[a-zA-Z]+)|(?:\\d+)|(?:=+)|(?:[^<>\\d=:$]+)|(?:[\\d=:$]+)',
  'g'
);

// console.log(goodRE, ambiguousRE, correctedRE, badRE);
// console.log(goodRE_replacement, ambiguousRE_replacement, correctedRE_replacement, badRE_replacement);

export function hyperlink_denson_song_number_markups(
  text,
  denson_year = '1991'
) {
  text = text.replace(goodRE, goodRE_replacement);
  text = text.replace(ambiguousRE, ambiguousRE_replacement);
  text = text.replace(correctedRE, correctedRE_replacement);
  text = text.replace(badRE, badRE_replacement);
  text = text.replace(/INSERT_YEAR_HERE/g, denson_year);

  // In order to help the user identify any number that looks
  // like a number but got skipped by the analyzer,
  // we highlight all possible song numbers with "maybesongnum".
  // This will give them a 'highlighter' style background color.
  // "maybesongnums" inside reconized spans will be neutralized via css, leaving only
  // the unrecognized ones showing.
  // The editor can then either ignore them, or, to clear the highlighting, can clear each one by
  // marking it as not-a-number "=38" or by forcing it to be recongized "[38]".

  // In this replacement, we both add the "maybesongnum" markup and also handle the =38 case.

  text = text.replace(any_number_tokenizer, token => {
    //	console.log(token);
    switch (true) {
      case !!token.match(/^=(\d+)$/): // handle the non-markup "=38" case: remove the = sign.
        return `<span class="notasongnum">${token.match(/^=(\d+)$/)[1]}</span>`;
      case !!token.match(/[<>()$:]/): // exclude HTML tags or song numbers already followed by abbrevs e.g. "22 (EH 2)" or dollar amounts or times
        return token;
      case !!token.match(/^(?:\b\d{2,3}[tbTB]?\b)$/): // if the token now matches the song regex, mark it as a maybesongnum.
        return `<span class="maybesongnum">${token}</span>`;
      default:
        // anything else also stays untouched.
        return token;
    }
  });

  return text;
}



// WEBPACK FOOTER //
// ./src/render_minutes_markup.js